// components/settings/surfox-settings/sections/MessagingSection.jsx
// Messaging Settings - TCPA Compliance
// Response timing is built-in and not customer-adjustable. The AI reply path draws
// 20-70s (processmessageai/lib/scoring.ts getRandomDelay, lowered from 20-90s 2026-09-14
// to buy back wall-clock margin — see HUMAN_DELAY_MAX_MS); the canned-acknowledgement
// path in handleincomingmessage sendDelayedSMS is separate and still draws 30-90s. The
// banner below reflects the AI reply path's numbers, not the canned-ack path's.
// Throughput is governed by the platform per A2P tier, not customer-adjustable

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../../../context/AuthContext';
import { PERMISSIONS } from '../../../../lib/permissions';
import supabase from '../../../../lib/supabaseClient';
import {
  Shield,
  CheckCircle,
  AlertCircle,
  Activity,
  Info,
  Loader2,
  Check
} from 'lucide-react';

// ============================================================================
// CONSTANTS
// ============================================================================

const TCPA_FREQUENCY_OPTIONS = [
  { value: 'initial', label: 'Initial Only', description: 'First message only' },
  { value: 'every', label: 'Every Message', description: 'Safest option' },
  { value: 'count', label: 'Every X Messages', description: 'Periodic reminder' },
  { value: 'days', label: 'Every X Days', description: 'Time-based interval' }
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === 'development') return 'http://localhost:3001';
  return 'https://api.getsurfox.com/api';
};

const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) throw new Error(`Authentication error: ${sessionError.message}`);
    if (!session?.access_token) throw new Error('No valid session found. Please log in again.');

    const apiBaseUrl = getApiBaseUrl();
    const fullUrl = `${apiBaseUrl}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      ...options.headers
    };

    const response = await fetch(fullUrl, { ...options, headers });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error(`API endpoint not found.`);
      }

      let errorMessage = `Request failed: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (parseError) {
        const responseText = await response.text();
        errorMessage = `${errorMessage}. Response: ${responseText.substring(0, 200)}...`;
      }
      throw new Error(errorMessage);
    }

    const responseText = await response.text();
    if (!responseText) return {};

    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      throw new Error(`Invalid JSON response from API`);
    }
  } catch (error) {
    console.error('API Request failed:', error);
    throw error;
  }
};

// ============================================================================
// SECTION CARD COMPONENT
// ============================================================================

const SectionCard = ({ icon: Icon, iconColor, title, subtitle, badge, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center space-x-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${iconColor}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h4 className="font-semibold text-slate-900">{title}</h4>
          {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {badge}
    </div>
    {children}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function MessagingSection({ onUpdate }) {
  const { user, hasPermission, loading: authLoading } = useAuth();
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Permission checks
  const canViewMessagingSettings = hasPermission(PERMISSIONS.VIEW_EDIT_AI_SETTINGS) || hasPermission(PERMISSIONS.MODIFY_AI_REPLY_MODE);
  const canEditMessagingSettings = hasPermission(PERMISSIONS.VIEW_EDIT_AI_SETTINGS);

  // TCPA Settings State
  const [tcpaEnabled, setTcpaEnabled] = useState(false);
  const [tcpaCustomText, setTcpaCustomText] = useState('');
  const [tcpaMode, setTcpaMode] = useState('standard');
  const [tcpaFrequencyMode, setTcpaFrequencyMode] = useState('initial');
  const [tcpaFrequencyValue, setTcpaFrequencyValue] = useState('5');

  useEffect(() => {
    const fetchSettings = async () => {
      if (authLoading) return;
      
      if (!user || !user.tenant_id) {
        setError('User or tenant information is missing.');
        setSettingsLoading(false);
        return;
      }

      if (!canViewMessagingSettings) {
        setSettingsLoading(false);
        return;
      }

      setSettingsLoading(true);
      setError('');
      try {
        const settings = await makeAuthenticatedRequest(`/settings?tenant_id=${user.tenant_id}`);

        setTcpaEnabled(settings['tcpa_compliance_enabled']?.value === 'true' || false);
        setTcpaCustomText(settings['tcpa_custom_text']?.value || '');
        setTcpaMode(settings['tcpa_custom_text']?.value ? 'custom' : 'standard');
        setTcpaFrequencyMode(settings['tcpa_frequency_mode']?.value || 'initial');
        setTcpaFrequencyValue(settings['tcpa_frequency_value']?.value || '5');
      } catch (err) {
        console.error('Error loading messaging settings:', err.message);
        setError(`Failed to load settings: ${err.message}`);
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchSettings();
  }, [user?.tenant_id, authLoading, canViewMessagingSettings]);

  const handleSave = async () => {
    if (!canEditMessagingSettings) {
      setError("You don't have permission to save messaging settings.");
      return;
    }

    if (!user || !user.tenant_id) {
      setError('User or tenant information is missing.');
      return;
    }

    setSaving(true);
    setSuccess('');
    setError('');

    const settingsPayload = {
      tcpa_compliance_enabled: { value: tcpaEnabled.toString() },
      tcpa_frequency_mode: { value: tcpaFrequencyMode },
      tcpa_frequency_value: { value: tcpaFrequencyValue }
    };

    if (tcpaMode === 'custom') {
      settingsPayload.tcpa_custom_text = { value: tcpaCustomText };
    }

    try {
      await makeAuthenticatedRequest('/settings', {
        method: 'PUT',
        body: JSON.stringify({ settings: settingsPayload, tenant_id: user.tenant_id })
      });

      setSuccess('Settings saved successfully!');
      onUpdate?.();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Permission check
  if (!canViewMessagingSettings && !authLoading) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-medium">Access Restricted</p>
        <p className="text-sm text-slate-400 mt-1">You don't have permission to view messaging settings.</p>
      </div>
    );
  }

  if (settingsLoading || authLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <span className="text-sm text-emerald-700 font-medium">{success}</span>
        </div>
      )}

      {/* Permission Warning */}
      {!canEditMessagingSettings && canViewMessagingSettings && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <span className="text-sm text-amber-700">
            Read-only access. Admin permissions required to make changes.
          </span>
        </div>
      )}

      {/* TCPA Compliance */}
      <SectionCard
        icon={Shield}
        iconColor="from-amber-500 to-amber-600"
        title="TCPA Compliance"
        subtitle="Opt-out language for messaging"
        badge={
          <label className="flex items-center space-x-2 cursor-pointer">
            <span className="text-xs text-slate-500">{tcpaEnabled ? 'Enabled' : 'Disabled'}</span>
            <div className="relative">
              <input
                type="checkbox"
                checked={tcpaEnabled}
                onChange={(e) => setTcpaEnabled(e.target.checked)}
                disabled={!canEditMessagingSettings}
                className="sr-only"
              />
              <div className={`w-10 h-6 rounded-full transition-colors ${
                tcpaEnabled ? 'bg-emerald-500' : 'bg-slate-300'
              } ${!canEditMessagingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  tcpaEnabled ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </div>
            </div>
          </label>
        }
      >
        {tcpaEnabled && (
          <div className="space-y-4">
            {/* TCPA Mode Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={() => setTcpaMode('standard')}
                disabled={!canEditMessagingSettings}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  !canEditMessagingSettings ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                } ${
                  tcpaMode === 'standard' 
                    ? 'border-orange-500 bg-orange-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="font-medium text-slate-900 mb-1">Standard Text</div>
                <p className="text-xs text-slate-500 italic">
                  "Reply STOP to opt out. Msg & data rates may apply."
                </p>
              </button>
              
              <button
                onClick={() => setTcpaMode('custom')}
                disabled={!canEditMessagingSettings}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  !canEditMessagingSettings ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                } ${
                  tcpaMode === 'custom' 
                    ? 'border-orange-500 bg-orange-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="font-medium text-slate-900 mb-1">Custom Text</div>
                <p className="text-xs text-slate-500">Create your own opt-out message</p>
              </button>
            </div>

            {/* Custom Text Input */}
            {tcpaMode === 'custom' && (
              <div>
                <textarea
                  value={tcpaCustomText}
                  onChange={(e) => setTcpaCustomText(e.target.value)}
                  disabled={!canEditMessagingSettings}
                  placeholder="Enter your custom TCPA compliance message..."
                  className="w-full h-20 px-4 py-3 border border-slate-200 rounded-xl text-sm
                             resize-none placeholder-slate-400
                             focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500
                             disabled:bg-slate-100 disabled:cursor-not-allowed"
                />
                <div className="flex items-start space-x-2 mt-2 p-3 bg-blue-50 border border-blue-100 rounded-xl">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700">
                    Include clear opt-out instructions (e.g., "Reply STOP") and data rate notice.
                  </p>
                </div>
              </div>
            )}

            {/* Frequency Selection */}
            <div>
              <p className="text-sm font-medium text-slate-700 mb-3">Frequency</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {TCPA_FREQUENCY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTcpaFrequencyMode(opt.value)}
                    disabled={!canEditMessagingSettings}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      !canEditMessagingSettings ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                    } ${
                      tcpaFrequencyMode === opt.value 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="text-sm font-medium text-slate-900">{opt.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>
                  </button>
                ))}
              </div>

              {/* Frequency Value Input */}
              {(tcpaFrequencyMode === 'count' || tcpaFrequencyMode === 'days') && (
                <div className="flex items-center space-x-3 mt-3 p-3 bg-slate-50 rounded-xl">
                  <span className="text-sm text-slate-600">Every</span>
                  <input
                    type="number"
                    min="1"
                    max={tcpaFrequencyMode === 'count' ? 50 : 90}
                    value={tcpaFrequencyValue}
                    onChange={(e) => setTcpaFrequencyValue(e.target.value)}
                    disabled={!canEditMessagingSettings}
                    className="w-16 px-3 py-1.5 border border-slate-200 rounded-lg text-center text-sm
                               focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500
                               disabled:bg-slate-100 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-slate-600">
                    {tcpaFrequencyMode === 'count' ? 'messages' : 'days'}
                  </span>
                </div>
              )}
            </div>

            {/* Legal Note */}
            <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-100 rounded-xl">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                TCPA requirements vary by industry. Consult legal counsel for your specific needs.
              </p>
            </div>
          </div>
        )}

        {!tcpaEnabled && (
          <div className="text-center py-6 text-slate-500 text-sm">
            Enable TCPA compliance to configure opt-out messaging
          </div>
        )}
      </SectionCard>

      {/* Configuration Summary */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-slate-200">
            <Activity className="w-4 h-4 text-slate-600" />
          </div>
          <span className="font-medium text-slate-900">Current Configuration</span>
        </div>
        
        <div className="bg-white rounded-lg p-3 border border-slate-200">
          <div className="text-xs text-slate-500 mb-1">TCPA Compliance</div>
          <div className="font-medium text-slate-900">{tcpaEnabled ? 'Enabled' : 'Disabled'}</div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-100 rounded-xl">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          AI response timing is built-in. Replies wait about 20 to 70 seconds before sending, so they arrive at a human pace rather than instantly.
        </p>
      </div>

      {/* Save Button */}
      {canEditMessagingSettings && (
        <div className="flex items-center justify-end pt-2">
          <motion.button
            onClick={handleSave}
            disabled={saving}
            whileTap={{ scale: 0.98 }}
            className={`
              px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200
              flex items-center space-x-2
              ${success 
                ? 'bg-emerald-500 text-white' 
                : 'bg-slate-900 text-white hover:bg-slate-800'
              }
              ${saving ? 'opacity-50 cursor-not-allowed' : 'shadow-sm hover:shadow'}
            `}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : success ? (
              <>
                <Check className="w-4 h-4" />
                <span>Saved</span>
              </>
            ) : (
              <span>Save Changes</span>
            )}
          </motion.button>
        </div>
      )}
    </div>
  );
}